/**
 * Managing one account.
 *
 * Everything a moderator needs about a person in one sheet: who they are, when
 * they were last here, what they have done, their badges, and the levers that
 * change their standing. The facts come from the profile endpoint's staff-only
 * block, which the server already returns to moderators - the search result
 * alone is far too thin to act on.
 *
 * Nothing here is a two-step. Picking a badge grants it, picking a role sets
 * it; the only things that stop to ask are the ones that lock somebody out or
 * hand them the keys.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import type { PublicUser, Tier } from '@/lib/types';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { Avatar } from '@/components/Avatar';
import { BadgeMark } from '@/components/BadgeMark';
import { Select } from '@/components/Select';
import { RoleTag, TierTag, UserName } from '@/components/user';
import { Button, Field, Input, Modal, Tag, useConfirm, useToast } from '@/components/ui';
import { Icon } from '@/icons/Icon';
import { cn } from '@/lib/cn';
import { AccountStanding, type Standing } from '@/components/Standing';

/** What moderation.js returns about one person. */
type ModEntry = {
  id: string;
  kind: string;
  reason?: string;
  minutes?: number;
  at: number;
  byName?: string;
};
type ModSummary = {
  warns: number;
  timeouts: number;
  silenced: { until: number; reason: string; minutes: number } | null;
  history: ModEntry[];
};

/** How long a suspension runs. 0 means it has no end date. */
const LENGTHS: [string, number][] = [
  ['1 day', 1], ['3 days', 3], ['1 week', 7],
  ['2 weeks', 14], ['30 days', 30], ['90 days', 90], ['Permanent', 0]
];

export function UserSheet({ user, isAdmin, onClose }: {
  user: PublicUser;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const catalog = useSession((s) => s.catalog);
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const { data } = useQuery({
    queryKey: ['user', user.username],
    queryFn: () => endpoints.user(user.username)
  });
  /*
   * Where they stand, and what put them there.
   *
   * A moderator opening somebody had the ban and nothing else: no way to see
   * that this is their fourth warning, or that the mute from Tuesday has
   * already lapsed. Deciding without that is deciding twice - once wrongly,
   * then again when somebody else notices.
   */
  const { data: record } = useQuery({
    queryKey: ['user', user.id, 'moderation'],
    queryFn: () => api.get<{ standing: Standing; moderation: ModSummary }>('/api/admin/users/' + user.id + '/moderation')
  });

  const full = data?.user ?? user;
  const staff = (data?.admin ?? {}) as Record<string, unknown>;
  const owned = new Set(full.badges ?? []);

  /* The suspension being composed. A week is the length a moderator reaches
     for when they have not thought about it, so it is where this starts. */
  const [days, setDays] = useState(7);
  const [custom, setCustom] = useState(false);
  const [reason, setReason] = useState('');

  async function act(path: string, body?: unknown, note?: string) {
    try {
      await api.post(path, body);
      if (note) toast(note, 'good');
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      await queryClient.invalidateQueries({ queryKey: ['user', user.username] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  /*
   * Six totals, then the dates.
   *
   * A moderator deciding what to do needs to know when somebody arrived and
   * when they were last seen far more than they need another counter, so the
   * counters stay small and the dates get the room.
   */
  const figures: [string, string | number][] = [
    ['Level', full.level],
    ['XP', num(staff.xp).toLocaleString()],
    ['Hosted', num(staff.partiesHosted ?? full.stats?.hosted)],
    ['Joined', num(staff.partiesJoined ?? full.stats?.joined)],
    ['Friends', num(staff.friends ?? full.stats?.friends)],
    ['Messages', num(staff.messages).toLocaleString()]
  ];

  const reports = num(staff.reports);
  const open = num(staff.openReports);

  const facts: [string, string][] = [
    ['Account id', full.id],
    ['Joined', when(staff.createdAt)],
    ['Last seen', when(staff.lastSeen)],
    ['Last message', staff.lastMessageAt ? when(staff.lastMessageAt) : 'Never'],
    ['Signed-in devices', String(num(staff.sessions))],
    ['Last sign-in', when(staff.lastSession)],
    ['Storage used', size(num(staff.uploads))],
    ['Reports against', reports ? (open ? open + ' open of ' + reports : String(reports)) : 'None']
  ];

  const privacy = (staff.settings ?? {}) as Record<string, string | boolean>;
  const audit = (staff.audit ?? []) as { at: number; action: string }[];

  /* When it would end, written out, so nobody has to count days forward in
     their head to know what they are about to do. */
  const ends = when(Date.now() + days * 86_400_000);

  async function suspend() {
    const ok = await confirm(
      (full.banned ? 'Change the suspension on ' : 'Suspend ') + full.displayName + '?',
      days > 0
        ? 'They are signed out everywhere and cannot sign back in until ' + ends + '.'
        : 'They are signed out everywhere and cannot sign back in. This one has no end date.',
      { confirmLabel: full.banned ? 'Update' : 'Suspend', danger: true }
    );
    if (!ok) return;
    await act(
      '/api/admin/users/' + full.id + '/ban',
      { days, reason: reason.trim() },
      full.banned ? 'Suspension updated' : 'Account suspended'
    );
    setReason('');
  }

  return (
    <Modal
      wide
      title={'Manage ' + full.displayName}
      subtitle={'@' + full.username}
      onClose={onClose}
      footer={<Button kind="outline" onClick={onClose}>Done</Button>}
    >
      <div className="flex flex-col gap-[var(--gap)]">
        {/* ---- who ---- */}
        <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-3">
          <Avatar user={full} size={54} presence />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <UserName user={full} className="text-[15px] font-bold" />
              <RoleTag role={full.role} />
              <TierTag tier={full.tier} />
              {full.banned ? <Tag kind="bad">SUSPENDED</Tag> : null}
            </div>
            <p className="mt-0.5 truncate text-[12px] text-mute">
              {full.customStatus || full.bio || 'No status set.'}
            </p>
          </div>
          <Button kind="outline" size="sm" onClick={() => { onClose(); go('u/' + full.username); }}>
            Open profile
          </Button>
        </div>

        {/* ---- where they stand, and what put them there ---- */}
        {record?.standing ? (
          <div className="rounded-[var(--radius)] border border-line-soft bg-surface-2 p-3.5">
            <AccountStanding standing={record.standing} />
            {record.moderation?.history?.length ? (
              <details className="mt-3 border-t border-line-soft pt-2.5">
                <summary className="cursor-pointer list-none text-[12px] font-bold text-mute select-none hover:text-ink">
                  Everything on record ({record.moderation.history.length})
                </summary>
                <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
                  {record.moderation.history.map((e) => (
                    <li key={e.id} className="flex flex-wrap items-baseline gap-x-2 rounded-[var(--radius-s)] bg-surface-1 px-2.5 py-1.5 text-[12.5px]">
                      <span className="font-semibold text-ink">
                        {e.kind === 'warn' ? 'Warned' : e.kind === 'timeout' ? 'Muted ' + (e.minutes ?? 0) + 'm' : e.kind === 'lifted' ? 'Mute lifted' : e.kind}
                      </span>
                      {e.reason ? <span className="min-w-0 flex-1 truncate text-dim">{e.reason}</span> : <span className="flex-1" />}
                      <span className="text-[11px] text-ghost">
                        {e.byName ? 'by ' + e.byName + ' · ' : ''}{when(e.at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        ) : null}

        {full.banned ? (
          <div className="rounded-[var(--radius)] border border-[color-mix(in_srgb,var(--bad)_45%,transparent)] bg-[color-mix(in_srgb,var(--bad)_9%,transparent)] p-3.5">
            <div className="flex flex-wrap items-center gap-2">
              <Icon name="ban" size={15} className="text-bad" />
              <span className="text-[13px] font-bold text-bad">Suspended</span>
              <span className="min-w-4 flex-1" />
              <Button
                kind="outline"
                size="sm"
                onClick={() => void act('/api/admin/users/' + full.id + '/unban', {}, 'Account restored')}
              >
                Restore account
              </Button>
            </div>
            <p className="mt-2 text-[12.5px] text-dim">{full.banned.reason || 'No reason was recorded.'}</p>
            <p className="mt-0.5 text-[11.5px] text-mute">
              {full.banned.until
                ? 'Ends ' + when(full.banned.until) + ' · ' + left(full.banned.until)
                : 'No end date - this one is permanent.'}
              {full.banned.at ? ' · Applied ' + when(full.banned.at) : ''}
            </p>
          </div>
        ) : null}

        {/* ---- the totals ---- */}
        <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(94px,1fr))]">
          {figures.map(([name, value]) => (
            <div key={name} className="rounded-[var(--radius-s)] border border-line-soft bg-surface-1 px-2.5 py-2">
              <div className="text-[9.5px] font-extrabold tracking-[.1em] text-ghost uppercase">{name}</div>
              <div className="mt-0.5 text-[17px] leading-tight font-extrabold tabular-nums">{value}</div>
            </div>
          ))}
        </div>

        {/* ---- the dates ---- */}
        <div className="grid gap-x-7 rounded-[var(--radius)] border border-line-soft bg-surface-1 px-3.5 py-1 sm:grid-cols-2">
          {/* Two columns means two last rows, so the one that ends the left
              column drops its rule as well - otherwise the grid finishes
              lopsided. */}
          {facts.map(([name, value]) => (
            <div key={name} className="flex items-baseline justify-between gap-3 border-b border-line-soft py-2 last:border-0 sm:[&:nth-last-child(2)]:border-0">
              <span className="flex-none text-[11.5px] text-mute">{name}</span>
              <span className="selectable truncate text-right text-[12.5px] font-semibold">{value}</span>
            </div>
          ))}
        </div>

        {/* ---- what they let other people do ---- */}
        {Object.keys(privacy).length ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <Icon name="lock" size={12} className="text-ghost" />
            {Object.entries(privacy).map(([key, value]) => (
              <span
                key={key}
                className="rounded-full border border-line-soft bg-surface-1 px-2.5 py-1 text-[11px] text-mute"
              >
                {readable(key)} <span className="font-semibold text-dim">{String(value)}</span>
              </span>
            ))}
          </div>
        ) : null}

        {/* ---- badges: one click on, one click off ---- */}
        <Block title="Badges" note="Pick one to grant it. Click a badge they have to take it back.">
          <div className="flex flex-wrap gap-1.5">
            {owned.size ? [...owned].map((id) => {
              const badge = catalog?.badges.find((b) => b.id === id);
              return (
                <button
                  key={id}
                  type="button"
                  title="Click to revoke"
                  onClick={() => void act('/api/admin/users/' + full.id + '/badge', { badgeId: id, revoke: true }, 'Badge revoked')}
                  className="group flex items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] border border-line bg-surface-2 py-1 pr-2 pl-1.5 text-[12px] font-semibold transition-colors hover:border-bad hover:text-bad"
                >
                  <BadgeMark badge={badge} size={18} />
                  {badge?.name ?? id}
                  <Icon name="close" size={11} className="opacity-45 group-hover:opacity-100" />
                </button>
              );
            }) : (
              <span className="text-[12.5px] text-mute">No badges yet.</span>
            )}
          </div>

          {/* No Grant button - choosing the badge is the whole action. */}
          <Select
            auto
            className="mt-2.5 min-w-[240px]"
            placeholder="Grant a badge…"
            value=""
            onChange={(id) => void act('/api/admin/users/' + full.id + '/badge', { badgeId: id }, 'Badge granted')}
            options={(catalog?.badges ?? [])
              .filter((b) => !owned.has(b.id))
              .map((b) => ({
                value: b.id, label: b.name, sub: b.desc,
                icon: b.icon, color: b.color, group: b.tier
              }))}
          />
        </Block>

        {/* ---- role and membership, admins only ---- */}
        {isAdmin ? (
          <Block title="Standing" note="Both take effect the moment you choose them.">
            <div className="grid gap-[var(--gap)] sm:grid-cols-2">
              <Field label="Role">
                <Select
                  search={false}
                  value={full.role}
                  onChange={(next) => {
                    if (next === full.role) return;
                    if (next !== 'admin') {
                      void act('/api/admin/users/' + full.id + '/role', { role: next }, 'Role updated');
                      return;
                    }
                    /* The one role that can undo everything you just did. */
                    void confirm(
                      'Make ' + full.displayName + ' an admin?',
                      'Admins can do everything you can, including changing your own role.',
                      { confirmLabel: 'Make admin', danger: true }
                    ).then((ok) => {
                      if (ok) void act('/api/admin/users/' + full.id + '/role', { role: next }, 'Role updated');
                    });
                  }}
                  options={[
                    { value: 'user', label: 'User', icon: 'profile' },
                    { value: 'mod', label: 'Moderator', sub: 'Reports and moderation', icon: 'shieldNav' },
                    { value: 'admin', label: 'Admin', sub: 'Everything', icon: 'crown' }
                  ]}
                />
              </Field>

              <Field label="Membership" hint="Premium and VIP unlock animated pictures and name colours.">
                <Select
                  search={false}
                  value={full.tier}
                  onChange={(next) => {
                    if (next !== full.tier) {
                      void act('/api/admin/users/' + full.id + '/tier', { tier: next as Tier }, 'Membership updated');
                    }
                  }}
                  options={[
                    { value: 'none', label: 'Standard' },
                    { value: 'premium', label: 'Premium', icon: 'badgePremium', color: 'var(--tier-epic)' },
                    { value: 'vip', label: 'VIP', icon: 'badgeVip', color: 'var(--tier-mythic)' }
                  ]}
                />
              </Field>
            </div>
          </Block>
        ) : null}

        {/* ---- suspension ---- */}
        <Block
          title={full.banned ? 'Change the suspension' : 'Suspend this account'}
          note="Signs them out everywhere. Choose how long and say why - the reason is what they are shown."
        >
          <div className="flex flex-wrap gap-1.5">
            {LENGTHS.map(([name, n]) => (
              <Chip key={name} on={!custom && days === n} onClick={() => { setCustom(false); setDays(n); }}>
                {name}
              </Chip>
            ))}
            <Chip on={custom} onClick={() => setCustom(true)}>Custom…</Chip>
          </div>

          {custom ? (
            <div className="mt-2 flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={3650}
                value={days || ''}
                placeholder="Days"
                onChange={(e) => setDays(Math.min(3650, Math.max(0, Math.floor(Number(e.target.value) || 0))))}
                className="w-[120px]"
              />
              <span className="text-[12px] text-mute">days, up to ten years</span>
            </div>
          ) : null}

          <Input
            value={reason}
            maxLength={200}
            placeholder="Reason - they will see this"
            onChange={(e) => setReason(e.target.value)}
            className="mt-2"
          />

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <span className="min-w-[180px] flex-1 text-[12px] text-mute">
              <Icon name="clock" size={12} className="mr-1 inline align-[-1px] text-ghost" />
              {days > 0 ? 'Ends ' + ends : 'Permanent - there is no end date.'}
            </span>
            <Button kind="danger" onClick={() => void suspend()} disabled={custom && days < 1}>
              {full.banned ? 'Update suspension' : 'Suspend'}
            </Button>
          </div>
        </Block>

        {/* ---- what has already been done ---- */}
        {audit.length ? (
          <Block title="Recent staff actions" note="The last twelve things done to or by this account.">
            <div className="flex flex-col">
              {audit.map((entry, i) => (
                <div
                  key={entry.at + ':' + i}
                  className="flex items-baseline gap-2.5 border-b border-line-soft py-1.5 text-[12px] last:border-0"
                >
                  <Icon name="history" size={12} className="flex-none translate-y-0.5 text-ghost" />
                  <span className="min-w-0 flex-1 truncate font-semibold text-dim">{readable(entry.action)}</span>
                  <span className="flex-none text-[11.5px] whitespace-nowrap text-mute">{when(entry.at)}</span>
                </div>
              ))}
            </div>
          </Block>
        ) : null}
      </div>
      {dialog}
    </Modal>
  );
}

/** A titled section of the sheet, with the rule above it. */
function Block({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-line-soft pt-[var(--gap)]">
      <span className="block text-[10.5px] font-bold tracking-[.07em] text-mute uppercase">{title}</span>
      {note ? <p className="mt-0.5 mb-2 text-[11.5px] text-ghost">{note}</p> : <span className="mb-2 block" />}
      {children}
    </section>
  );
}

/** One choice in a row of choices. */
function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'h-[30px] rounded-[calc(10px*var(--radius-mul,1))] border px-3 text-[12px] font-bold transition-colors',
        on
          ? 'border-accent-line bg-accent-soft text-accent-hi'
          : 'border-line-soft bg-surface-1 text-mute hover:border-line hover:text-dim'
      )}
    >
      {children}
    </button>
  );
}

/** Whatever the server sent, as a number, without NaN leaking onto the page. */
function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** A timestamp, written the way a person would write it. */
function when(value: unknown): string {
  const ms = num(value);
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

/** How much of a suspension is still to run. */
function left(until: number): string {
  const ms = until - Date.now();
  if (ms <= 0) return 'already over';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return days + (days === 1 ? ' day left' : ' days left');
  const hours = Math.max(1, Math.round(ms / 3_600_000));
  return hours + (hours === 1 ? ' hour left' : ' hours left');
}

/** Bytes, at the scale a person reads them. */
function size(bytes: number): string {
  if (!bytes) return 'Nothing uploaded';
  const mb = bytes / 1_048_576;
  if (mb < 1) return Math.max(1, Math.round(bytes / 1024)) + ' KB';
  if (mb < 1024) return mb.toFixed(mb < 10 ? 1 : 0) + ' MB';
  return (mb / 1024).toFixed(2) + ' GB';
}

/** `allowFriendRequests` and `user.ban` both come out readable. */
function readable(key: string): string {
  const words = key
    .replace(/[._]/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
